import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { RestaurantCapture } from "@/features/recipe-book/RestaurantCapture";
import { cn } from "@/lib/utils";
import type { RecipeBookEntry } from "@/types/recipeBook";

const SOURCE_CHIPS = [
  { key: "all", label: "Tous" },
  { key: "favorite", label: "Favoris" },
  { key: "manual", label: "Manuels" },
  { key: "restaurant_capture", label: "Restaurant" },
] as const;

type SourceFilter = (typeof SOURCE_CHIPS)[number]["key"];

// ── Page ───────────────────────────────────────────────────────────────

export function RecipeBookPage() {
  const [entries, setEntries] = useState<RecipeBookEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<SourceFilter>("all");

  useEffect(() => {
    setIsLoading(true);
    const params = filter !== "all" ? `?source=${filter}` : "";
    api<RecipeBookEntry[]>(`/api/v1/favorites/recipe-book${params}`)
      .then(setEntries)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [filter]);

  return (
    <div className="flex h-full flex-col bg-background pb-2">
      <header className="border-b border-hair px-5 py-4">
        <h1 className="h-display text-[28px] leading-[1.05]">
          Mes <span className="italic">recettes</span>
        </h1>
      </header>

      {/* Source filter chips */}
      <div className="flex gap-2 overflow-x-auto border-b border-hair px-5 py-3">
        {SOURCE_CHIPS.map((chip) => (
          <button
            key={chip.key}
            onClick={() => setFilter(chip.key)}
            className={cn(
              "shrink-0 rounded-full border px-3.5 py-1.5 text-[12px] tracking-[0.04em] transition-colors",
              filter === chip.key
                ? "border-foreground bg-foreground text-background"
                : "border-hair bg-background text-ink-50 hover:border-ink-30",
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-5 py-4">
        {/* Restaurant capture tile */}
        <div className="mb-4">
          <RestaurantCapture />
        </div>

        {isLoading && (
          <p className="py-12 text-center text-[13px] text-ink-50">
            Chargement…
          </p>
        )}
        {!isLoading && entries.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <Heart className="size-10 text-ink-30" strokeWidth={1.25} />
            <p className="font-serif text-lg italic text-ink-50">
              Aucune recette sauvegardée
            </p>
            <p className="max-w-xs text-[13px] text-ink-50">
              Ajoutez des recettes en favoris depuis votre menu pour les
              retrouver ici.
            </p>
          </div>
        )}
        {!isLoading && entries.length > 0 && (
          <div className="grid gap-3">
            {entries.map((entry) => (
              <RecipeBookCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────

function RecipeBookCard({ entry }: { entry: RecipeBookEntry }) {
  const payload = entry.payload;
  const title =
    typeof payload.title === "string"
      ? payload.title
      : entry.recipe_id.replace(/-/g, " ");
  const summary = typeof payload.summary === "string" ? payload.summary : null;

  const sourceLabel =
    entry.source === "favorite"
      ? "Favori"
      : entry.source === "manual"
        ? "Manuel"
        : "Restaurant";

  return (
    <Link
      // Book entries open their own detail view: the recipe may not be in
      // the current week's plan (audit H13).
      to={`/recipe-book/${entry.id}`}
      className="flex items-start gap-4 border border-hair bg-background p-4 transition-colors hover:border-foreground"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/10">
        <Heart
          className="size-5 text-accent"
          strokeWidth={1.5}
          fill="currentColor"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-serif text-[16px] leading-[1.15]">
            {capitalize(title)}
          </span>
          <span className="shrink-0 rounded-full bg-paper px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] text-ink-50">
            {sourceLabel}
          </span>
        </div>
        {summary && (
          <p className="mt-1.5 line-clamp-2 text-[12.5px] leading-snug text-ink-50">
            {summary}
          </p>
        )}
      </div>
    </Link>
  );
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
